<template>
  <div class="page">
    <div class="card">
      <div v-if="isAdmin">
        <div class="page-head">
          <h1>Статистика</h1>
          <p>Общая информация по заявкам</p>
        </div>

        <p v-if="errorMessage" class="message error">{{ errorMessage }}</p>

        <div class="stats-grid">
          <div class="stat-card stat-card--dark">
            <span>Всего заявок</span>
            <strong>{{ statistics.total }}</strong>
          </div>

          <div class="stat-card">
            <span>Активные</span>
            <strong>{{ activeOrders }}</strong>
            <small>Принято + В процессе</small>
          </div>

          <div class="stat-card">
            <span>Завершено</span>
            <strong>{{ statistics.completed }}</strong>
            <small>{{ completedPercent }}% от всех</small>
          </div>

          <div class="stat-card">
            <span>Отклонено</span>
            <strong>{{ statistics.rejected }}</strong>
            <small>{{ rejectedPercent }}% от всех</small>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-card__head">
            <h2>График по статусам</h2>
            <span>Количество заявок</span>
          </div>

          <div class="chart">
            <div
              v-for="item in chartItems"
              :key="item.key"
              class="chart-item"
            >
              <div class="chart-bar-wrap">
                <div
                  class="chart-bar"
                  :style="{ height: `${item.percent}%` }"
                >
                  <span class="chart-bar__value">{{ item.value }}</span>
                </div>
              </div>

              <div class="chart-label">{{ item.label }}</div>
            </div>
          </div>
        </div>

        <div class="info-grid">
          <div class="info-card">
            <span>В работе сейчас</span>
            <strong>{{ activeOrders }}</strong>
            <p>Это заявки со статусами «Принято» и «В процессе».</p>
          </div>

          <div class="info-card">
            <span>Процент завершения</span>
            <strong>{{ completedPercent }}%</strong>
            <p>Доля заявок со статусом «Завершено» от общего числа.</p>
          </div>

          <div class="info-card">
            <span>Процент отказов</span>
            <strong>{{ rejectedPercent }}%</strong>
            <p>Доля заявок со статусом «Отклонено» от общего числа.</p>
          </div>

          <div class="info-card">
            <span>Чаще всего сейчас</span>
            <strong>{{ dominantStatus.label }}</strong>
            <p>Количество: {{ dominantStatus.value }}</p>
          </div>
        </div>

        <div class="status-list-card">
          <div class="status-list-card__head">
            <h2>Сводка по статусам</h2>
          </div>

          <div class="status-list">
            <div class="status-row">
              <span>Создана</span>
              <strong>{{ statistics.created }}</strong>
            </div>

            <div class="status-row">
              <span>Принято</span>
              <strong>{{ statistics.accepted }}</strong>
            </div>

            <div class="status-row">
              <span>В процессе</span>
              <strong>{{ statistics.in_progress }}</strong>
            </div>

            <div class="status-row">
              <span>Отклонено</span>
              <strong>{{ statistics.rejected }}</strong>
            </div>

            <div class="status-row">
              <span>Завершено</span>
              <strong>{{ statistics.completed }}</strong>
            </div>
          </div>
        </div>

        <div class="admin-actions">
          <button class="submit-btn" type="button" :disabled="statsLoading" @click="reloadStats">
            {{ statsLoading ? 'Обновляем...' : 'Обновить статистику' }}
          </button>
        </div>
      </div>

      <div v-else>
        <div class="page-head">
          <h1>Создать заявку</h1>
          <p>Заполни данные для нового заказа</p>
        </div>

        <form class="form" @submit.prevent="submitRequest">
          <div class="field">
            <div class="field-head">
              <label>Название изделия</label>
              <span class="counter">{{ form.product_name.length }}/{{ limits.product_name }}</span>
            </div>
            <input
              :value="form.product_name"
              type="text"
              :maxlength="limits.product_name"
              placeholder="Например: Кровать прямая 90х200"
              required
              @input="onTextInput('product_name', $event, limits.product_name)"
            />
          </div>

          <div class="field">
            <div class="field-head">
              <label>Вид рисунка изголовья</label>
              <span class="counter">{{ form.headboard_pattern.length }}/{{ limits.headboard_pattern }}</span>
            </div>
            <input
              :value="form.headboard_pattern"
              type="text"
              :maxlength="limits.headboard_pattern"
              placeholder="Опишите рисунок"
              @input="onTextInput('headboard_pattern', $event, limits.headboard_pattern)"
            />
          </div>

          <div class="field">
            <div class="field-head">
              <label>Название ткани</label>
              <span class="counter">{{ form.fabric_name.length }}/{{ limits.fabric_name }}</span>
            </div>
            <input
              :value="form.fabric_name"
              type="text"
              :maxlength="limits.fabric_name"
              placeholder="Название ткани"
              required
              @input="onTextInput('fabric_name', $event, limits.fabric_name)"
            />
          </div>

          <div class="field">
            <div class="field-head">
              <label>Количество штук</label>
              <span class="counter">макс. 9999</span>
            </div>
            <input
              :value="form.quantity"
              type="number"
              min="1"
              max="9999"
              placeholder="1"
              required
              @input="onQuantityInput"
            />
          </div>

          <div class="field">
            <label>Срок</label>
            <input
              v-model="form.deadline_date"
              type="date"
              required
            />
          </div>

          <div class="field">
            <div class="field-head">
              <label>Комментарий</label>
              <span class="counter">{{ form.comment.length }}/{{ limits.comment }}</span>
            </div>
            <textarea
              :value="form.comment"
              rows="4"
              :maxlength="limits.comment"
              placeholder="Дополнительные пожелания"
              @input="onTextInput('comment', $event, limits.comment)"
            ></textarea>
          </div>

          <div class="field">
            <label>Фото</label>

            <div class="upload-row">
              <button type="button" class="file-btn" @click="openFilePicker">
                Выбрать фото
              </button>

              <span class="file-text">
                {{ photos.length ? `Выбрано: ${photos.length}` : 'Файл не выбран' }}
              </span>
            </div>

            <input
              ref="fileInput"
              class="hidden-file-input"
              type="file"
              multiple
              accept="image/*"
              @change="handleFiles"
            />

            <div class="upload-hint">Можно добавить до 3 фото</div>
          </div>

          <div v-if="photos.length" class="preview-grid">
            <div v-for="(file, index) in photos" :key="file.name + file.size + index" class="preview-card">
              <img :src="file.preview" alt="" class="preview-image" />
              <button type="button" class="remove-preview-btn" @click="removePhoto(index)">
                Удалить
              </button>
            </div>
          </div>

          <p v-if="errorMessage" class="message error">{{ errorMessage }}</p>

          <button class="submit-btn" type="submit" :disabled="createLoading">
            {{ createLoading ? 'Сохраняем...' : 'Отправить заявку' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HomeView',
  data() {
    return {
      limits: {
        product_name: 120,
        headboard_pattern: 120,
        fabric_name: 80,
        comment: 500
      },
      form: {
        product_name: '',
        headboard_pattern: '',
        fabric_name: '',
        quantity: 1,
        comment: '',
        deadline_date: ''
      },
      photos: []
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    },
    createLoading() {
      return this.$store.getters['orders/createLoading']
    },
    statsLoading() {
      return this.$store.getters['orders/statsLoading']
    },
    statistics() {
      return this.$store.getters['orders/statistics']
    },
    errorMessage() {
      return this.$store.getters['orders/error']
    },

    activeOrders() {
      return Number(this.statistics.accepted || 0) + Number(this.statistics.in_progress || 0)
    },

    completedPercent() {
      const total = Number(this.statistics.total || 0)
      if (!total) return 0
      return Math.round((Number(this.statistics.completed || 0) / total) * 100)
    },

    rejectedPercent() {
      const total = Number(this.statistics.total || 0)
      if (!total) return 0
      return Math.round((Number(this.statistics.rejected || 0) / total) * 100)
    },

    maxChartValue() {
      return Math.max(
        Number(this.statistics.created || 0),
        Number(this.statistics.accepted || 0),
        Number(this.statistics.in_progress || 0),
        Number(this.statistics.rejected || 0),
        Number(this.statistics.completed || 0),
        1
      )
    },

    chartItems() {
      const rows = [
        {
          key: 'created',
          label: 'Создана',
          value: Number(this.statistics.created || 0)
        },
        {
          key: 'accepted',
          label: 'Принято',
          value: Number(this.statistics.accepted || 0)
        },
        {
          key: 'in_progress',
          label: 'В процессе',
          value: Number(this.statistics.in_progress || 0)
        },
        {
          key: 'rejected',
          label: 'Отклонено',
          value: Number(this.statistics.rejected || 0)
        },
        {
          key: 'completed',
          label: 'Завершено',
          value: Number(this.statistics.completed || 0)
        }
      ]

      return rows.map((item) => ({
        ...item,
        percent: Math.max(10, Math.round((item.value / this.maxChartValue) * 100))
      }))
    },

    dominantStatus() {
      const rows = [
        { label: 'Создана', value: Number(this.statistics.created || 0) },
        { label: 'Принято', value: Number(this.statistics.accepted || 0) },
        { label: 'В процессе', value: Number(this.statistics.in_progress || 0) },
        { label: 'Отклонено', value: Number(this.statistics.rejected || 0) },
        { label: 'Завершено', value: Number(this.statistics.completed || 0) }
      ]

      return rows.sort((a, b) => b.value - a.value)[0] || { label: '—', value: 0 }
    }
  },
  async mounted() {
    if (this.isAdmin) {
      await this.$store.dispatch('orders/fetchStatistics')
    }
  },
  methods: {
    normalizeText(value) {
      return String(value || '')
        .replace(/\s{2,}/g, ' ')
        .replace(/^\s+/g, '')
    },

    onTextInput(field, event, maxLength) {
      const value = this.normalizeText(event.target.value).slice(0, maxLength)
      this.form[field] = value
      event.target.value = value
    },

    onQuantityInput(event) {
      let value = String(event.target.value || '').replace(/[^\d]/g, '')
      value = value.slice(0, 4)

      if (!value) {
        this.form.quantity = ''
        event.target.value = ''
        return
      }

      let numeric = Number(value)

      if (numeric < 1) numeric = 1
      if (numeric > 9999) numeric = 9999

      this.form.quantity = numeric
      event.target.value = String(numeric)
    },

    openFilePicker() {
      this.$refs.fileInput.click()
    },

    handleFiles(event) {
      const selectedFiles = Array.from(event.target.files || [])
      const availableSlots = 3 - this.photos.length

      if (availableSlots <= 0) {
        event.target.value = ''
        return
      }

      const nextFiles = selectedFiles
        .slice(0, availableSlots)
        .map((file) => ({
          file,
          name: file.name,
          size: file.size,
          preview: URL.createObjectURL(file)
        }))

      this.photos = [...this.photos, ...nextFiles]
      event.target.value = ''
    },

    removePhoto(index) {
      const photo = this.photos[index]
      if (photo?.preview) {
        URL.revokeObjectURL(photo.preview)
      }
      this.photos.splice(index, 1)
    },

    async submitRequest() {
      const payload = {
        ...this.form,
        product_name: this.form.product_name.trim(),
        headboard_pattern: this.form.headboard_pattern.trim(),
        fabric_name: this.form.fabric_name.trim(),
        comment: this.form.comment.trim(),
        quantity: Number(this.form.quantity),
        photos: this.photos.map((item) => item.file)
      }

      if (!payload.product_name || !payload.fabric_name || !payload.deadline_date || !payload.quantity) {
        return
      }

      const result = await this.$store.dispatch('orders/createOrder', payload)

      if (!result || !result.ok) {
        return
      }

      this.photos.forEach((item) => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview)
        }
      })

      this.form = {
        product_name: '',
        headboard_pattern: '',
        fabric_name: '',
        quantity: 1,
        comment: '',
        deadline_date: ''
      }
      this.photos = []

      this.$router.push(`/requests/${result.order?.order_number}`)
    },

    async reloadStats() {
      await this.$store.dispatch('orders/fetchStatistics')
    }
  },
  beforeUnmount() {
    this.photos.forEach((item) => {
      if (item.preview) {
        URL.revokeObjectURL(item.preview)
      }
    })
  }
}
</script>

<style scoped>
.page {
  padding: 12px;
}

.card {
  max-width: 760px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 22px;
  padding: 22px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
}

.page-head h1 {
  margin: 0 0 8px;
  font-size: 30px;
}

.page-head p {
  margin: 0 0 22px;
  color: #6b7280;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field label {
  display: block;
  margin-bottom: 8px;
  font-weight: 700;
}

.field-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.counter {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 100;
  white-space: nowrap;
}

.field input,
.field textarea {
  width: 100%;
  border: 1px solid #dbe3f0;
  border-radius: 14px;
  padding: 14px 16px;
  background: #f8fafc;
  outline: none;
}

.field textarea {
  resize: vertical;
}

.hidden-file-input {
  display: none;
}

.upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.file-btn {
  border: none;
  border-radius: 14px;
  background: #111827;
  color: #ffffff;
  padding: 12px 16px;
  font-weight: 700;
  cursor: pointer;
}

.file-text,
.upload-hint {
  color: #6b7280;
}

.upload-hint {
  margin-top: 6px;
}

.preview-grid,
.stats-grid,
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.info-grid {
  margin-top: 16px
}

.preview-card,
.stat-card,
.chart-card,
.info-card,
.status-list-card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 14px;
  background: #ffffff;
}

.stat-card {
  min-height: 116px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stat-card span,
.info-card span {
  color: #6b7280;
  font-weight: 700;
}

.stat-card strong,
.info-card strong {
  font-size: 34px;
  line-height: 1;
}

.stat-card small,
.info-card p {
  color: #6b7280;
}

.stat-card--dark {
  background: #111827;
  color: #ffffff;
}

.stat-card--dark span {
  color: rgba(255, 255, 255, 0.75);
}

.chart-card,
.status-list-card {
  margin-top: 16px;
}

.chart-card__head,
.status-list-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.chart-card__head h2,
.status-list-card__head h2 {
  margin: 0;
  font-size: 20px;
}

.chart-card__head span {
  color: #6b7280;
  font-size: 14px;
}

.chart {
  height: 260px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  align-items: end;
}

.chart-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  height: 100%;
}

.chart-bar-wrap {
  width: 100%;
  height: 210px;
  display: flex;
  align-items: end;
  background: #f8fafc;
  border-radius: 14px;
  padding: 10px;
}

.chart-bar {
  width: 100%;
  min-height: 20px;
  border-radius: 12px;
  background: linear-gradient(180deg, #111827 0%, #374151 100%);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 8px;
  transition: height 0.3s ease;
}

.chart-bar__value {
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
}

.chart-label {
  text-align: center;
  font-size: 12px;
  color: #475569;
  font-weight: 700;
}

.info-card {
  min-height: 128px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-card p {
  margin: 0;
  line-height: 1.4;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px 14px;
}

.status-row span {
  color: #475569;
  font-weight: 700;
}

.status-row strong {
  color: #111827;
}

.admin-actions {
  margin-top: 18px;
}

.preview-image {
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 10px;
}

.remove-preview-btn {
  width: 100%;
  border: 1px solid #fecaca;
  border-radius: 12px;
  background: #fff1f2;
  color: #be123c;
  padding: 10px 12px;
  font-weight: 700;
  cursor: pointer;
}

.submit-btn {
  border: none;
  border-radius: 14px;
  background: #111827;
  color: #ffffff;
  padding: 14px 16px;
  font-weight: 700;
  cursor: pointer;
}

.submit-btn[disabled] {
  opacity: 0.65;
  cursor: default;
}

.message {
  margin: 0 0 16px;
  padding: 14px 16px;
  border-radius: 14px;
}

.message.error {
  background: #f7dede;
  color: #c52f2f;
}

@media (max-width: 768px) {
  .preview-grid,
  .stats-grid,
  .info-grid {
    grid-template-columns: 1fr;
  }

  .chart {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 8px;
    height: 220px;
  }

  .chart-bar-wrap {
    height: 170px;
    padding: 8px;
  }

  .chart-label {
    font-size: 10px;
  }

  .stat-card strong,
  .info-card strong {
    font-size: 28px;
  }

 
}
</style>